// 游戏详情（Phase 4.2）：8 tabs。
// - tab 状态走 ?tab= search param。
// - 老 #fragment 链接由 __root.tsx 的 applyHashMigration 转换。

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Layers,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { Button } from "@/components/ui/Button";
import { Field, Input, Textarea } from "@/components/ui/Input";
import { Card, CardTitle } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Checkbox";
import { Dialog } from "@/components/ui-lite/Dialog";
import { QueryView } from "@/components/ui/QueryView";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  clearGameModuleConfig,
  createCurrencyWatch,
  createMaterialWatch,
  deleteCurrencyWatch,
  deleteContinent,
  deleteMap,
  deleteMaterialWatch,
  getGameModuleReferences,
  getGameBundle,
  getGameMap,
  getGameProfitItems,
  getGameProfitInstances,
  getGameProfitMaps,
  listGameModules,
  listProfitSourceRules,
  listProfitValueRules,
  updateCurrencyWatch,
  updateGameModuleConfig,
  updateGameJSONField,
  updateMaterialWatch,
  upsertProfitSourceRule,
  upsertProfitValueRule,
  upsertContinent,
  upsertGame,
  upsertMap,
  type ContinentUpsertInput,
  type GameBundleResponse,
  type GameContinent,
  type GameMap,
  type GameMapSummary,
  type MapUpsertInput,
  type CurrencyWatchRecord,
  type MaterialWatchRecord,
  type ProfitSourceRuleInput,
  type ProfitValueRuleInput,
} from "@/api/games";
import type {
  GameModuleEntry,
  GameModuleReferencesResponse,
  ProfitItemAggregate,
  ProfitInstanceAggregate,
  ProfitMapAggregate,
  ProfitSourceRule,
  ProfitValueRule,
} from "@/api/types";
import { MapEditor, type MapEditorSubmitInput } from "@/components/games/MapEditor";
import { ServerGroupsPanel } from "@/components/games/ServerGroupsPanel";
import { AutoMapHuntRuntimePanel } from "@/components/games/AutoMapHuntRuntimePanel";
import {
  CopyDrawer,
  planToUpserts,
  type CopyPlanRequest,
} from "@/components/copy-drawer/CopyDrawer";
import { ConditionBuilder } from "@/components/condition-builder/ConditionBuilder";
import {
  ConditionSchema,
  type Condition,
} from "@/components/condition-builder/schema";
import {
  formatModuleConfigDrift,
  formatModuleDesiredEnabled,
  formatModuleRuntimeStatus,
} from "@/lib/module-status";
import { compactNumber, formatDurationSec, formatMs } from "@/lib/format";
import { useSSESubscription } from "@/hooks/useSSESubscription";
import {
  AutoAuctionBidConfigForm,
  AutoSummonConfigForm,
  AutoSuoYaoConfigForm,
  ContinentFleeConfigForm,
  ModuleConfigFields,
  ModuleToggle,
  PullMonsterConfigForm,
  parseAutoMapHuntDesired,
  parseJSONRecord,
} from "@/components/modules/ModuleConfigForms";

const TABS = [
  "basic",
  "global",
  "protect",
  "assistedCruise",
  "patrolTemplates",
  "continents",
  "maps",
  "servers",
  "autoMapHunt",
  "modules",
  "currencies",
  "materials",
  "profit",
] as const;
type TabKey = (typeof TABS)[number];

const MAP_PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200] as const;

interface GameDetailSearch {
  tab?: TabKey;
}

function parseSearch(input: Record<string, unknown>): GameDetailSearch {
  const tab = typeof input.tab === "string" ? input.tab : undefined;
  if (tab && (TABS as readonly string[]).includes(tab)) {
    return { tab: tab as TabKey };
  }
  return {};
}

export const Route = createFileRoute("/games/$gameId")({
  validateSearch: parseSearch,
  component: GameDetailRoute,
});

function GameDetailRoute() {
  const { gameId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/games/$gameId" });
  const tab: TabKey = search.tab ?? "basic";

  return (
    <GameDetailView
      gameID={gameId}
      tab={tab}
      onTabChange={(next) =>
        navigate({ search: { tab: next === "basic" ? undefined : next } })
      }
    />
  );
}

export interface GameDetailViewProps {
  gameID: string;
  tab: TabKey;
  onTabChange: (next: TabKey) => void;
}

export function GameDetailView({ gameID, tab, onTabChange }: GameDetailViewProps) {
  const q = useQuery({
    queryKey: ["game-bundle", gameID],
    queryFn: () => getGameBundle(gameID),
  });

  return (
    <div className="space-y-4">
      <QueryView query={q}>
        {(data) => (
          <Tabs value={tab} onValueChange={(v) => onTabChange(v as TabKey)}>
            <TabsList>
              <TabsTrigger value="basic">基础</TabsTrigger>
              <TabsTrigger value="global">global</TabsTrigger>
              <TabsTrigger value="protect">protect</TabsTrigger>
              <TabsTrigger value="assistedCruise">随机飞</TabsTrigger>
              <TabsTrigger value="patrolTemplates">观察点模板</TabsTrigger>
              <TabsTrigger value="continents">大陆</TabsTrigger>
              <TabsTrigger value="maps">地图</TabsTrigger>
              <TabsTrigger value="servers">区服</TabsTrigger>
              <TabsTrigger value="autoMapHunt">自动下图观测</TabsTrigger>
              <TabsTrigger value="modules">模块</TabsTrigger>
              <TabsTrigger value="currencies">货币</TabsTrigger>
              <TabsTrigger value="materials">物品</TabsTrigger>
              <TabsTrigger value="profit">收益</TabsTrigger>
            </TabsList>

            <TabsContent value="basic">
              <BasicTab bundle={data} />
            </TabsContent>
            <TabsContent value="global">
              <JSONFieldTab bundle={data} field="global" />
            </TabsContent>
            <TabsContent value="protect">
              <JSONFieldTab bundle={data} field="protect" />
            </TabsContent>
            <TabsContent value="assistedCruise">
              <AssistedCruiseRandomFlyTab bundle={data} />
            </TabsContent>
            <TabsContent value="patrolTemplates">
              <PatrolTemplatesTab bundle={data} />
            </TabsContent>
            <TabsContent value="continents">
              <ContinentsTab bundle={data} />
            </TabsContent>
            <TabsContent value="maps">
              <MapsTab bundle={data} />
            </TabsContent>
            <TabsContent value="servers">
              <ServerGroupsPanel gameID={gameID} />
            </TabsContent>
            <TabsContent value="autoMapHunt">
              <AutoMapHuntRuntimePanel gameID={gameID} maps={data.bundle.maps} />
            </TabsContent>
            <TabsContent value="modules">
              <ModulesTab gameID={gameID} />
            </TabsContent>
            <TabsContent value="currencies">
              <WatchListTab kind="currencies" bundle={data} />
            </TabsContent>
            <TabsContent value="materials">
              <WatchListTab kind="materials" bundle={data} />
            </TabsContent>
            <TabsContent value="profit">
              <ProfitRulesTab gameID={gameID} bundle={data} />
            </TabsContent>
          </Tabs>
        )}
      </QueryView>
    </div>
  );
}

// ===== Basic =====

function BasicTab({ bundle }: { bundle: GameBundleResponse }) {
  const queryClient = useQueryClient();
  const profile = bundle.bundle.profile;
  const [displayName, setDisplayName] = useState(profile.display_name);
  const [notes, setNotes] = useState(profile.notes);

  const mut = useMutation({
    mutationFn: () =>
      upsertGame({
        game_id: profile.game_id,
        display_name: displayName,
        notes,
        version: profile.version,
      }),
    onSuccess: () => {
      toast.success("已保存");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", profile.game_id] });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    mut.mutate();
  }

  return (
    <Card className="p-4">
      <CardTitle>基础信息</CardTitle>
      <form className="space-y-3" onSubmit={submit} noValidate>
        <Field label="game_id">
          <Input value={profile.game_id} disabled />
        </Field>
        <Field label="显示名">
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>
        <Field label="备注">
          <Textarea
            value={notes}
            rows={3}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" size="sm" disabled={mut.isPending}>
            {mut.isPending ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ===== global / protect JSON =====

function JSONFieldTab({
  bundle,
  field,
}: {
  bundle: GameBundleResponse;
  field: "global" | "protect" | "patrolTemplates";
}) {
  const queryClient = useQueryClient();
  const initial = useMemo(() => {
    try {
      const fallback = field === "patrolTemplates" ? [] : {};
      return JSON.stringify(bundle.bundle[field] ?? fallback, null, 2);
    } catch {
      return field === "patrolTemplates" ? "[]" : "{}";
    }
  }, [bundle, field]);

  const [text, setText] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const parsed = JSON.parse(text);
      return updateGameJSONField(
        bundle.bundle.game_id,
        field,
        parsed,
        bundle.bundle.version,
      );
    },
    onSuccess: () => {
      toast.success(`已保存 ${field}`);
      queryClient.invalidateQueries({
        queryKey: ["game-bundle", bundle.bundle.game_id],
      });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  function onChange(next: string) {
    setText(next);
    try {
      JSON.parse(next);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Card className="p-4">
      <CardTitle>{field} JSON</CardTitle>
      <Field error={error ?? undefined}>
        <Textarea
          rows={20}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
        />
      </Field>
      <div className="mt-3 flex justify-end">
        <Button
          size="sm"
          disabled={!!error || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? "保存中…" : "保存"}
        </Button>
      </div>
    </Card>
  );
}

interface PatrolTemplatePointDraft {
  label: string;
  x: string;
  y: string;
  range: string;
}

interface PatrolTemplateDraft {
  id: string;
  name: string;
  mapDataID: string;
  waypointRange: string;
  visionRange: string;
  coverageThreshold: string;
  status: string;
  assistedCruiseEnabled: boolean;
  highPowerDropAvoidEnabled: boolean;
  highPowerDropAvoidRaw: Record<string, unknown>;
  points: PatrolTemplatePointDraft[];
}

interface AssistedCruiseTaskDraft {
  text: string;
  error: string | null;
}

type AssistedCruiseTaskKey =
  | "openTask"
  | "recordTask"
  | "selectTask"
  | "startTask"
  | "stopTask";

interface AssistedCruiseRandomFlyDraft {
  enabled: boolean;
  recordSlot: string;
  openTimeout: string;
  prepareCooldown: string;
  triggerCooldown: string;
  openTask: AssistedCruiseTaskDraft;
  recordTask: AssistedCruiseTaskDraft;
  selectTask: AssistedCruiseTaskDraft;
  startTask: AssistedCruiseTaskDraft;
  stopTask: AssistedCruiseTaskDraft;
}

const DEFAULT_ASSISTED_CRUISE_RANDOM_FLY = {
  enabled: false,
  recordSlot: 1,
  openTask: { index: 107, subid: 109, act: "@游戏托管main" },
  recordTask: {
    Act: "@记录地图#记录位置={slot}",
    index: 999999999,
    UserID: "999999999",
  },
  selectTask: {
    Act: "@选定托管地图",
    index: 999999999,
    MenuItem: [{ id: "S$选定托管地图", value: "" }],
    UserID: "999999999",
  },
  startTask: {
    Act: "@开始托管系统",
    index: 999999999,
    UserID: "999999999",
  },
  stopTask: {
    Act: "@停止托管系统",
    index: 999999999,
    UserID: "999999999",
  },
  openTimeout: 2,
  prepareCooldown: 5,
  triggerCooldown: 8,
} as const;

function assistedCruiseTaskFromValue(value: unknown, fallback: unknown): AssistedCruiseTaskDraft {
  const source = asRecord(value) ?? asRecord(fallback) ?? {};
  return {
    text: JSON.stringify(source, null, 2),
    error: null,
  };
}

function assistedCruiseDraftFromValue(value: unknown): AssistedCruiseRandomFlyDraft {
  const record = asRecord(value) ?? {};
  const defaults = DEFAULT_ASSISTED_CRUISE_RANDOM_FLY;
  return {
    enabled: booleanFromUnknown(record.enabled, defaults.enabled),
    recordSlot: stringFromUnknown(record.recordSlot) || String(defaults.recordSlot),
    openTimeout: stringFromUnknown(record.openTimeout) || String(defaults.openTimeout),
    prepareCooldown:
      stringFromUnknown(record.prepareCooldown) || String(defaults.prepareCooldown),
    triggerCooldown:
      stringFromUnknown(record.triggerCooldown) || String(defaults.triggerCooldown),
    openTask: assistedCruiseTaskFromValue(record.openTask, defaults.openTask),
    recordTask: assistedCruiseTaskFromValue(record.recordTask, defaults.recordTask),
    selectTask: assistedCruiseTaskFromValue(record.selectTask, defaults.selectTask),
    startTask: assistedCruiseTaskFromValue(record.startTask, defaults.startTask),
    stopTask: assistedCruiseTaskFromValue(record.stopTask, defaults.stopTask),
  };
}

function validateObjectJSON(text: string): string | null {
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return "必须是 JSON object";
    }
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

function parseTaskJSON(task: AssistedCruiseTaskDraft, label: string) {
  const error = validateObjectJSON(task.text);
  if (error) throw new Error(`${label}: ${error}`);
  return JSON.parse(task.text) as Record<string, unknown>;
}

function buildAssistedCruisePayload(draft: AssistedCruiseRandomFlyDraft) {
  return {
    enabled: draft.enabled,
    recordSlot: numberField(draft.recordSlot, 1, "记录位置"),
    openTask: parseTaskJSON(draft.openTask, "打开托管面板"),
    recordTask: parseTaskJSON(draft.recordTask, "记录当前地图"),
    selectTask: parseTaskJSON(draft.selectTask, "选定托管地图"),
    startTask: parseTaskJSON(draft.startTask, "开始托管"),
    stopTask: parseTaskJSON(draft.stopTask, "停止托管"),
    openTimeout: numberField(draft.openTimeout, 2, "开窗超时"),
    prepareCooldown: numberField(draft.prepareCooldown, 5, "准备重试冷却"),
    triggerCooldown: numberField(draft.triggerCooldown, 8, "触发冷却"),
  };
}

function AssistedCruiseRandomFlyTab({ bundle }: { bundle: GameBundleResponse }) {
  const queryClient = useQueryClient();
  const gameID = bundle.bundle.game_id;
  const [draft, setDraft] = useState<AssistedCruiseRandomFlyDraft>(() =>
    assistedCruiseDraftFromValue(bundle.bundle.assistedCruiseRandomFly),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(assistedCruiseDraftFromValue(bundle.bundle.assistedCruiseRandomFly));
    setError(null);
  }, [bundle.bundle.assistedCruiseRandomFly]);

  const mut = useMutation({
    mutationFn: () => {
      const payload = buildAssistedCruisePayload(draft);
      return updateGameJSONField(
        gameID,
        "assistedCruiseRandomFly",
        payload,
        bundle.bundle.version,
      );
    },
    onSuccess: () => {
      toast.success("已保存随机飞配置");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  function update<K extends keyof AssistedCruiseRandomFlyDraft>(
    key: K,
    value: AssistedCruiseRandomFlyDraft[K],
  ) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function updateTask(key: AssistedCruiseTaskKey, text: string) {
    setDraft((prev) => ({
      ...prev,
      [key]: {
        text,
        error: validateObjectJSON(text),
      },
    }));
  }

  function submit() {
    try {
      buildAssistedCruisePayload(draft);
      setError(null);
      mut.mutate();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const hasTaskError = [
    draft.openTask,
    draft.recordTask,
    draft.selectTask,
    draft.startTask,
    draft.stopTask,
  ].some((task) => task.error);

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <CardTitle>游戏托管随机飞</CardTitle>
        <Button size="sm" disabled={mut.isPending || hasTaskError} onClick={submit}>
          {mut.isPending ? "保存中…" : "保存"}
        </Button>
      </div>
      {error ? (
        <div className="mb-3 rounded border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 p-2 text-xs text-[hsl(var(--destructive))]">
          {error}
        </div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-4">
        <label className="flex items-center gap-2 rounded border border-[hsl(var(--border))] px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
          />
          启用游戏托管随机飞
        </label>
        <Field label="记录位置">
          <Input
            type="number"
            min={1}
            value={draft.recordSlot}
            onChange={(e) => update("recordSlot", e.target.value)}
          />
        </Field>
        <Field label="开窗超时">
          <Input
            type="number"
            min={0.1}
            step={0.1}
            value={draft.openTimeout}
            onChange={(e) => update("openTimeout", e.target.value)}
          />
        </Field>
        <Field label="准备重试冷却">
          <Input
            type="number"
            min={0}
            step={0.1}
            value={draft.prepareCooldown}
            onChange={(e) => update("prepareCooldown", e.target.value)}
          />
        </Field>
        <Field label="触发冷却">
          <Input
            type="number"
            min={0}
            step={0.1}
            value={draft.triggerCooldown}
            onChange={(e) => update("triggerCooldown", e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {(
          [
            ["openTask", "打开托管面板"],
            ["recordTask", "记录当前地图"],
            ["selectTask", "选定托管地图"],
            ["startTask", "开始托管"],
            ["stopTask", "停止托管"],
          ] as const
        ).map(([key, label]) => {
          const task = draft[key];
          return (
            <Field key={key} label={`${label} JSON`} error={task.error ?? undefined}>
              <Textarea
                rows={6}
                value={task.text}
                onChange={(e) => updateTask(key, e.target.value)}
                spellCheck={false}
              />
            </Field>
          );
        })}
      </div>
    </Card>
  );
}

function PatrolTemplatesTab({ bundle }: { bundle: GameBundleResponse }) {
  const queryClient = useQueryClient();
  const gameID = bundle.bundle.game_id;
  const [templates, setTemplates] = useState<PatrolTemplateDraft[]>(() =>
    patrolTemplateDraftsFromValue(bundle.bundle.patrolTemplates),
  );
  const [expandedTemplateIndex, setExpandedTemplateIndex] = useState<number | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTemplates(patrolTemplateDraftsFromValue(bundle.bundle.patrolTemplates));
    setExpandedTemplateIndex(null);
    setError(null);
  }, [bundle.bundle.patrolTemplates]);

  const mut = useMutation({
    mutationFn: () => {
      const payload = buildPatrolTemplatesPayload(templates);
      return updateGameJSONField(gameID, "patrolTemplates", payload, bundle.bundle.version);
    },
    onSuccess: () => {
      toast.success("已保存观察点模板");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  function updateTemplate(index: number, patch: Partial<PatrolTemplateDraft>) {
    setTemplates((prev) =>
      prev.map((template, i) =>
        i === index ? { ...template, ...patch } : template,
      ),
    );
  }

  function updatePoint(
    templateIndex: number,
    pointIndex: number,
    patch: Partial<PatrolTemplatePointDraft>,
  ) {
    setTemplates((prev) =>
      prev.map((template, i) =>
        i === templateIndex
          ? {
              ...template,
              points: template.points.map((point, j) =>
                j === pointIndex ? { ...point, ...patch } : point,
              ),
            }
          : template,
      ),
    );
  }

  function addTemplate() {
    setExpandedTemplateIndex(templates.length);
    setTemplates((prev) => [
      ...prev,
      {
        id: `mapdata_new_auto_v${prev.length + 1}`,
        name: "",
        mapDataID: "",
        waypointRange: "4",
        visionRange: "13",
        coverageThreshold: "1",
        status: "pending_confirm",
        assistedCruiseEnabled: false,
        highPowerDropAvoidEnabled: false,
        highPowerDropAvoidRaw: {},
        points: [],
      },
    ]);
  }

  function removeTemplate(index: number) {
    setExpandedTemplateIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
    setTemplates((prev) => prev.filter((_, i) => i !== index));
  }

  function addPoint(templateIndex: number) {
    setTemplates((prev) =>
      prev.map((template, i) =>
        i === templateIndex
          ? {
              ...template,
              points: [
                ...template.points,
                {
                  label: `auto_${template.points.length + 1}`,
                  x: "",
                  y: "",
                  range: template.waypointRange || "4",
                },
              ],
            }
          : template,
      ),
    );
  }

  function removePoint(templateIndex: number, pointIndex: number) {
    setTemplates((prev) =>
      prev.map((template, i) =>
        i === templateIndex
          ? {
              ...template,
              points: template.points.filter((_, j) => j !== pointIndex),
            }
          : template,
      ),
    );
  }

  function submit() {
    try {
      buildPatrolTemplatesPayload(templates);
      setError(null);
      mut.mutate();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <CardTitle>观察点模板</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addTemplate}>
            新建模板
          </Button>
          <Button size="sm" disabled={mut.isPending} onClick={submit}>
            {mut.isPending ? "保存中…" : "保存"}
          </Button>
        </div>
      </div>
      {error ? (
        <div className="mb-3 rounded border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10 p-2 text-xs text-[hsl(var(--destructive))]">
          {error}
        </div>
      ) : null}
      {templates.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          未配置观察点模板。
        </p>
      ) : (
        <div className="space-y-2">
          {templates.map((template, templateIndex) => {
            const expanded = expandedTemplateIndex === templateIndex;
            return (
              <div
                key={`${template.id}:${templateIndex}`}
                className="rounded border border-[hsl(var(--border))]"
              >
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[hsl(var(--muted))]"
                  aria-expanded={expanded}
                  onClick={() =>
                    setExpandedTemplateIndex((prev) =>
                      prev === templateIndex ? null : templateIndex,
                    )
                  }
                >
                  {expanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {template.id || `未命名模板 ${templateIndex + 1}`}
                    </span>
                    <PatrolMapSummary template={template} />
                  </span>
                </button>
                {expanded ? (
                  <div className="border-t border-[hsl(var(--border))] p-3">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div className="grid flex-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <Field label="模板 ID">
                          <Input
                            value={template.id}
                            onChange={(e) =>
                              updateTemplate(templateIndex, { id: e.target.value })
                            }
                          />
                        </Field>
                        <Field label="名称">
                          <Input
                            value={template.name}
                            onChange={(e) =>
                              updateTemplate(templateIndex, {
                                name: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="mapDataID">
                          <Input
                            value={template.mapDataID}
                            onChange={(e) =>
                              updateTemplate(templateIndex, {
                                mapDataID: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="到点半径">
                          <Input
                            type="number"
                            min={0}
                            value={template.waypointRange}
                            onChange={(e) =>
                              updateTemplate(templateIndex, {
                                waypointRange: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="视野半径">
                          <Input
                            type="number"
                            min={1}
                            value={template.visionRange}
                            onChange={(e) =>
                              updateTemplate(templateIndex, {
                                visionRange: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="覆盖阈值">
                          <Input
                            type="number"
                            min={0.1}
                            max={1}
                            step={0.05}
                            value={template.coverageThreshold}
                            onChange={(e) =>
                              updateTemplate(templateIndex, {
                                coverageThreshold: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <label className="flex items-center gap-2 rounded border border-[hsl(var(--border))] px-3 py-2 text-sm">
                          <input
                            type="checkbox"
                            checked={template.assistedCruiseEnabled}
                            onChange={(e) =>
                              updateTemplate(templateIndex, {
                                assistedCruiseEnabled: e.target.checked,
                              })
                            }
                          />
                          遇人随机飞
                        </label>
                        <label className="flex items-center gap-2 rounded border border-[hsl(var(--border))] px-3 py-2 text-sm">
                          <input
                            type="checkbox"
                            checked={template.highPowerDropAvoidEnabled}
                            onChange={(e) =>
                              updateTemplate(templateIndex, {
                                highPowerDropAvoidEnabled: e.target.checked,
                              })
                            }
                          />
                          高战力回避
                        </label>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeTemplate(templateIndex)}
                      >
                        删除模板
                      </Button>
                    </div>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">
                        点位 {template.points.length} 个
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addPoint(templateIndex)}
                      >
                        新增点位
                      </Button>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
                        <tr>
                          <th className="py-2 pr-3">label</th>
                          <th className="py-2 pr-3">x</th>
                          <th className="py-2 pr-3">y</th>
                          <th className="py-2 pr-3">range</th>
                          <th className="py-2 pr-3">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {template.points.map((point, pointIndex) => (
                          <tr
                            key={`${point.label}:${pointIndex}`}
                            className="border-b border-[hsl(var(--border))]/60"
                          >
                            <td className="py-1.5 pr-3">
                              <Input
                                value={point.label}
                                onChange={(e) =>
                                  updatePoint(templateIndex, pointIndex, {
                                    label: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td className="py-1.5 pr-3">
                              <Input
                                type="number"
                                value={point.x}
                                onChange={(e) =>
                                  updatePoint(templateIndex, pointIndex, {
                                    x: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td className="py-1.5 pr-3">
                              <Input
                                type="number"
                                value={point.y}
                                onChange={(e) =>
                                  updatePoint(templateIndex, pointIndex, {
                                    y: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td className="py-1.5 pr-3">
                              <Input
                                type="number"
                                min={0}
                                value={point.range}
                                onChange={(e) =>
                                  updatePoint(templateIndex, pointIndex, {
                                    range: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td className="py-1.5 pr-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  removePoint(templateIndex, pointIndex)
                                }
                              >
                                删除
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {template.points.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="py-4 text-xs text-[hsl(var(--muted-foreground))]"
                            >
                              暂无点位。
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function PatrolMapSummary({ template }: { template: PatrolTemplateDraft }) {
  const summary = patrolMapSummaryFromTemplate(template);
  return (
    <span className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
      {template.name ? <span>{template.name}</span> : null}
      <span>mapDataID {summary.mapDataID}</span>
      <span>候选点 {summary.pointCount} 个</span>
      <span>视野 {summary.visionRangeLabel}</span>
      <span>阈值 {summary.thresholdLabel}</span>
      {template.assistedCruiseEnabled ? <span>遇人随机飞</span> : null}
      {template.highPowerDropAvoidEnabled ? <span>高战力回避</span> : null}
      {template.status ? <span>{template.status}</span> : null}
    </span>
  );
}

function patrolMapSummaryFromTemplate(template: PatrolTemplateDraft) {
  return {
    mapDataID: template.mapDataID || "未填",
    pointCount: template.points.length,
    visionRangeLabel: formatPatrolSummaryNumber(template.visionRange, "13"),
    thresholdLabel: formatPatrolThreshold(template.coverageThreshold),
  };
}

function formatPatrolSummaryNumber(raw: string, fallback: string) {
  const text = raw.trim();
  return text || fallback;
}

function formatPatrolThreshold(raw: string) {
  const text = raw.trim();
  const value = Number(text || "1");
  if (!Number.isFinite(value)) return text || "100%";
  if (value <= 1) return `${Math.round(value * 100)}%`;
  return String(value);
}

function patrolTemplateDraftsFromValue(value: unknown): PatrolTemplateDraft[] {
  if (!Array.isArray(value)) return [];
  return value.map((item, index) => {
    const record = asRecord(item) ?? {};
    const points = Array.isArray(record.points) ? record.points : [];
    const assistedCruise = asRecord(record.assistedCruiseRandomFly) ?? {};
    const highPowerDropAvoid = asRecord(record.highPowerDropAvoid) ?? {};
    return {
      id: stringFromUnknown(record.id) || `template_${index + 1}`,
      name: stringFromUnknown(record.name),
      mapDataID: stringFromUnknown(record.mapDataID ?? record.map_data_id),
      waypointRange: stringFromUnknown(record.waypointRange) || "4",
      visionRange: stringFromUnknown(record.visionRange) || "13",
      coverageThreshold: stringFromUnknown(record.coverageThreshold) || "1",
      status: stringFromUnknown(record.status),
      assistedCruiseEnabled: booleanFromUnknown(assistedCruise.enabled, false),
      highPowerDropAvoidEnabled: booleanFromUnknown(highPowerDropAvoid.enabled, false),
      highPowerDropAvoidRaw: highPowerDropAvoid,
      points: points.map((point, pointIndex) => {
        const pointRecord = asRecord(point) ?? {};
        return {
          label: stringFromUnknown(pointRecord.label) || `auto_${pointIndex + 1}`,
          x: stringFromUnknown(pointRecord.x),
          y: stringFromUnknown(pointRecord.y),
          range: stringFromUnknown(pointRecord.range),
        };
      }),
    };
  });
}

function buildPatrolTemplatesPayload(templates: PatrolTemplateDraft[]) {
  const seen = new Set<string>();
  return templates.map((template, index) => {
    const id = template.id.trim();
    if (!id) throw new Error(`模板 ${index + 1} 缺少 ID`);
    if (seen.has(id)) throw new Error(`模板 ID 重复：${id}`);
    seen.add(id);
    return {
      id,
      name: template.name.trim(),
      mapDataID: template.mapDataID.trim(),
      waypointRange: numberField(template.waypointRange, 4, `模板 ${id} 到点半径`),
      visionRange: numberField(template.visionRange, 13, `模板 ${id} 视野半径`),
      coverageThreshold: numberField(
        template.coverageThreshold,
        1,
        `模板 ${id} 覆盖阈值`,
      ),
      ...(template.status.trim() ? { status: template.status.trim() } : {}),
      assistedCruiseRandomFly: {
        enabled: template.assistedCruiseEnabled,
      },
      highPowerDropAvoid: {
        ...template.highPowerDropAvoidRaw,
        enabled: template.highPowerDropAvoidEnabled,
      },
      points: template.points.map((point, pointIndex) => {
        const x = numberField(point.x, undefined, `模板 ${id} 点位 ${pointIndex + 1} x`);
        const y = numberField(point.y, undefined, `模板 ${id} 点位 ${pointIndex + 1} y`);
        return {
          x,
          y,
          ...(point.label.trim() ? { label: point.label.trim() } : {}),
          ...(point.range.trim()
            ? { range: numberField(point.range, undefined, `模板 ${id} 点位 ${pointIndex + 1} range`) }
            : {}),
        };
      }),
    };
  });
}

function numberField(raw: string, fallback: number | undefined, label: string) {
  const text = raw.trim();
  if (!text && fallback !== undefined) return fallback;
  const value = Number(text);
  if (!Number.isFinite(value)) {
    throw new Error(`${label} 必须是数字`);
  }
  return value;
}

function stringFromUnknown(value: unknown) {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

function booleanFromUnknown(value: unknown, fallback = false) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(text)) return true;
    if (["0", "false", "no", "off"].includes(text)) return false;
  }
  return fallback;
}

// ===== Continents =====

function ContinentsTab({ bundle }: { bundle: GameBundleResponse }) {
  const queryClient = useQueryClient();
  const gameID = bundle.bundle.game_id;
  const [editing, setEditing] = useState<GameContinent | null>(null);
  const [creating, setCreating] = useState(false);

  const upMut = useMutation({
    mutationFn: (input: ContinentUpsertInput) => upsertContinent(gameID, input),
    onSuccess: () => {
      toast.success("已保存大陆");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
      setEditing(null);
      setCreating(false);
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  const delMut = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      deleteContinent(gameID, id, version),
    onSuccess: () => {
      toast.success("已删除大陆");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
    },
    onError: (e) => toast.error(`删除失败：${(e as Error).message}`),
  });

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <CardTitle>大陆</CardTitle>
        <Button size="sm" onClick={() => setCreating(true)}>
          新建大陆
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
          <tr>
            <th className="py-2 pr-3">continent_id</th>
            <th className="py-2 pr-3">name</th>
            <th className="py-2 pr-3">启用</th>
            <th className="py-2 pr-3">排序</th>
            <th className="py-2 pr-3">入口</th>
            <th className="py-2 pr-3">map_count</th>
            <th className="py-2 pr-3">操作</th>
          </tr>
        </thead>
        <tbody>
          {bundle.bundle.continents.map((c) => (
            <tr
              key={c.continent_id}
              className="border-b border-[hsl(var(--border))]/60 align-top"
            >
              <td className="py-1.5 pr-3 font-mono text-xs">{c.continent_id}</td>
              <td className="py-1.5 pr-3">{c.name}</td>
              <td className="py-1.5 pr-3">
                {c.enabled ? (
                  <StatusBadge variant="ok">启用</StatusBadge>
                ) : (
                  <StatusBadge variant="neutral">停用</StatusBadge>
                )}
              </td>
              <td className="py-1.5 pr-3">{c.sort_order}</td>
              <td className="py-1.5 pr-3">
                {hasJSONArrayItems(c.enter) ? (
                  <StatusBadge variant="ok">已配置</StatusBadge>
                ) : (
                  <StatusBadge variant="warn">未配置</StatusBadge>
                )}
              </td>
              <td className="py-1.5 pr-3">{c.map_count ?? 0}</td>
              <td className="py-1.5 pr-3 space-x-1">
                <Button size="sm" variant="outline" onClick={() => setEditing(c)}>
                  编辑
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (
                      typeof window !== "undefined" &&
                      window.confirm(`删除大陆 ${c.continent_id}？`)
                    ) {
                      delMut.mutate({ id: c.continent_id, version: c.version });
                    }
                  }}
                >
                  删除
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog
        open={creating || !!editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={editing ? "编辑大陆" : "新建大陆"}
        className="w-[min(860px,96vw)]"
      >
        <ContinentForm
          initial={editing ?? undefined}
          existingContinentIDs={bundle.bundle.continents.map(
            (continent) => continent.continent_id,
          )}
          submitting={upMut.isPending}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={(values) => upMut.mutate(values)}
        />
      </Dialog>
    </Card>
  );
}

function ContinentForm({
  initial,
  existingContinentIDs,
  submitting,
  onCancel,
  onSubmit,
}: {
  initial?: GameContinent;
  existingContinentIDs?: string[];
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (input: ContinentUpsertInput) => void;
}) {
  const [continentID, setContinentID] = useState(initial?.continent_id ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [sortOrder, setSortOrder] = useState(String(initial?.sort_order ?? 0));
  const [enterJSON, setEnterJSON] = useState<ContinentJSONFieldState>(
    continentJSONFieldFrom(initial?.enter, "[]"),
  );
  const [conditions, setConditions] = useState<Condition[]>(
    safeParseConditions(initial?.enterConditions),
  );
  const initialContinentID = initial?.continent_id.trim() ?? "";
  const continentIDError = (() => {
    const nextID = continentID.trim();
    if (!nextID) return "continent_id 必填";
    if (
      nextID !== initialContinentID &&
      (existingContinentIDs ?? []).some((id) => id.trim() === nextID)
    ) {
      return "continent_id 已存在";
    }
    return null;
  })();

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (continentIDError || !name.trim()) return;
    const enter = parseContinentJSONField(enterJSON);
    if (enter.error) {
      setEnterJSON((current) => ({ ...current, error: enter.error }));
      return;
    }
    onSubmit({
      id: initial?.id,
      continent_id: continentID.trim(),
      name: name.trim(),
      enabled,
      enter: enter.value ?? [],
      enterConditions: conditions,
      sort_order: Number(sortOrder) || 0,
      version: initial?.version,
    });
  }

  const canSubmit =
    !submitting && !continentIDError && !!name.trim() && !enterJSON.error;

  return (
    <form className="space-y-3" onSubmit={submit} noValidate>
      <Field
        label="continent_id"
        htmlFor="continent_id"
        error={continentIDError ?? undefined}
      >
        <Input
          id="continent_id"
          value={continentID}
          onChange={(e) => setContinentID(e.target.value)}
        />
      </Field>
      <Field label="name">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="sort_order">
        <Input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </Field>
      <Field
        label="enter (steps JSON)"
        htmlFor="continent_enter_steps"
        error={enterJSON.error ?? undefined}
      >
        <Textarea
          id="continent_enter_steps"
          rows={8}
          value={enterJSON.text}
          onChange={(e) =>
            setEnterJSON({
              text: e.target.value,
              error: validateContinentJSON(e.target.value),
            })
          }
          spellCheck={false}
        />
      </Field>
      <div className="space-y-2">
        <h4 className="text-xs font-semibold">enterConditions</h4>
        <ConditionBuilder value={conditions} onChange={setConditions} />
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        启用
      </label>
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          取消
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {submitting ? "保存中…" : "保存"}
        </Button>
      </div>
    </form>
  );
}

interface ContinentJSONFieldState {
  text: string;
  error: string | null;
}

function continentJSONFieldFrom(
  value: unknown,
  fallback: string,
): ContinentJSONFieldState {
  if (value === undefined || value === null) {
    return { text: fallback, error: null };
  }
  try {
    return { text: JSON.stringify(value, null, 2), error: null };
  } catch {
    return { text: fallback, error: null };
  }
}

function validateContinentJSON(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    JSON.parse(trimmed);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

function parseContinentJSONField(
  state: ContinentJSONFieldState,
): { value: unknown; error: string | null } {
  try {
    return {
      value: state.text.trim() ? JSON.parse(state.text) : [],
      error: null,
    };
  } catch (e) {
    return { value: null, error: (e as Error).message };
  }
}

function safeParseConditions(value: unknown): Condition[] {
  if (!Array.isArray(value)) return [];
  const out: Condition[] = [];
  for (const item of value) {
    const parsed = ConditionSchema.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

function hasJSONArrayItems(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

// ===== Maps =====

interface MapFilters {
  search: string;
  continent: string;
  selectableOnly: boolean;
  orphansOnly: boolean;
  unreachableOnly: boolean;
}

function MapsTab({ bundle }: { bundle: GameBundleResponse }) {
  const queryClient = useQueryClient();
  const gameID = bundle.bundle.game_id;
  const [filters, setFilters] = useState<MapFilters>({
    search: "",
    continent: "",
    selectableOnly: false,
    orphansOnly: false,
    unreachableOnly: false,
  });
  const [editing, setEditing] = useState<GameMap | null>(null);
  const [creating, setCreating] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] =
    useState<(typeof MAP_PAGE_SIZE_OPTIONS)[number]>(20);
  const [loadingEditMapID, setLoadingEditMapID] = useState<string | null>(null);

  const visibleMaps = useMemo(
    () => filterMaps(bundle.bundle.maps, filters),
    [bundle.bundle.maps, filters],
  );
  const pageCount = Math.max(1, Math.ceil(visibleMaps.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = visibleMaps.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, visibleMaps.length);
  const pagedMaps = useMemo(
    () => visibleMaps.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [currentPage, pageSize, visibleMaps],
  );

  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const continentNameByID = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of bundle.bundle.continents) {
      if (c.name) map.set(c.continent_id, c.name);
    }
    return map;
  }, [bundle.bundle.continents]);

  const upMut = useMutation({
    mutationFn: (input: MapEditorSubmitInput) => upsertMapSubmission(gameID, input),
    onSuccess: (saved) => {
      toast.success(saved.length > 1 ? `已保存 ${saved.length} 张地图` : "已保存地图");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
      setEditing(null);
      setCreating(false);
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  // "保存并继续新建"：成功后不关 Dialog，表单状态完整保留，用户改 map_id / name /
  // match 等少量字段就能继续建下一张图。
  const continueMut = useMutation({
    mutationFn: (input: MapEditorSubmitInput) => upsertMapSubmission(gameID, input),
    onSuccess: (saved) => {
      toast.success(`已保存 ${saved.join("、")}，可继续新建`);
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  const delMut = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      deleteMap(gameID, id, version),
    onSuccess: () => {
      toast.success("已删除地图");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
    },
    onError: (e) => toast.error(`删除失败：${(e as Error).message}`),
  });

  async function openMapEditor(mapID: string) {
    setLoadingEditMapID(mapID);
    try {
      const response = await getGameMap(gameID, mapID);
      setEditing(response.map);
    } catch (e) {
      toast.error(`读取地图失败：${(e as Error).message}`);
    } finally {
      setLoadingEditMapID(null);
    }
  }

  async function buildCopyUpserts(request: CopyPlanRequest): Promise<MapUpsertInput[]> {
    const [sourceResponse, ...targetResponses] = await Promise.all([
      getGameMap(gameID, request.sourceID),
      ...request.targetIDs.map((mapID) => getGameMap(gameID, mapID)),
    ]);
    return planToUpserts({
      source: sourceResponse.map,
      targets: targetResponses.map((response) => response.map),
      fields: request.fields,
    });
  }

  const copyMut = useMutation({
    mutationFn: async (request: CopyPlanRequest) => {
      const plans = await buildCopyUpserts(request);
      for (const input of plans) {
        await upsertMap(gameID, input);
      }
    },
    onSuccess: () => {
      toast.success("批量复制完成");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
      setCopyOpen(false);
    },
    onError: (e) => toast.error(`批量复制失败：${(e as Error).message}`),
  });

  const batchCreateMut = useMutation({
    mutationFn: async (upserts: MapUpsertInput[]) => {
      // 串行落库，失败立即中断，下游 toast 报具体哪个 map_id 出错。
      const ok: string[] = [];
      for (const input of upserts) {
        try {
          await upsertMap(gameID, input);
          ok.push(input.map_id);
        } catch (e) {
          throw new Error(
            `${input.map_id}: ${e instanceof Error ? e.message : String(e)}（已完成 ${ok.length}/${upserts.length}）`,
            { cause: e },
          );
        }
      }
      return ok;
    },
    onSuccess: (ok) => {
      toast.success(`批量新建完成（${ok.length} 张）`);
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
      setCopyOpen(false);
    },
    onError: (e) => toast.error(`批量新建失败：${(e as Error).message}`),
  });

  const sameGroupOf = (m: Partial<GameMap>): GameMapSummary[] => {
    if (!m.nested_group) return [];
    return bundle.bundle.maps.filter(
      (other) =>
        other.nested_group === m.nested_group && other.map_id !== m.map_id,
    );
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <CardTitle>地图（{visibleMaps.length}/{bundle.bundle.maps.length}）</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setCopyOpen(true)}>
              批量复制
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              新建地图
            </Button>
          </div>
        </div>

        <MapsFilterBar
          filters={filters}
          continents={bundle.bundle.continents}
          onChange={setFilters}
        />

        <table className="mt-3 w-full text-sm">
          <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
            <tr>
              <th className="py-2 pr-3">map_id</th>
              <th className="py-2 pr-3">name</th>
              <th className="py-2 pr-3">大陆</th>
              <th className="py-2 pr-3">nested_group</th>
              <th className="py-2 pr-3">状态</th>
              <th className="py-2 pr-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {pagedMaps.map((m) => (
              <tr
                key={m.map_id}
                className="border-b border-[hsl(var(--border))]/60 align-top"
              >
                <td className="py-1.5 pr-3 font-mono text-xs">{m.map_id}</td>
                <td className="py-1.5 pr-3">{m.name}</td>
                <td className="py-1.5 pr-3">
                  {continentNameByID.get(m.continent_id) ?? (
                    <span className="font-mono text-xs">{m.continent_id}</span>
                  )}
                </td>
                <td className="py-1.5 pr-3 font-mono text-xs">{m.nested_group}</td>
                <td className="py-1.5 pr-3">
                  {m.unreachable ? (
                    <StatusBadge variant="warn">不可达</StatusBadge>
                  ) : m.selectable_default ? (
                    <StatusBadge variant="ok">可挂</StatusBadge>
                  ) : (
                    <StatusBadge variant="neutral">不可挂</StatusBadge>
                  )}
                </td>
                <td className="py-1.5 pr-3 space-x-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={loadingEditMapID === m.map_id}
                    onClick={() => void openMapEditor(m.map_id)}
                  >
                    {loadingEditMapID === m.map_id ? "读取中…" : "编辑"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (
                        typeof window !== "undefined" &&
                        window.confirm(`删除地图 ${m.map_id}？`)
                      ) {
                        delMut.mutate({ id: m.map_id, version: m.version });
                      }
                    }}
                  >
                    删除
                  </Button>
                </td>
              </tr>
            ))}
            {pagedMaps.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="py-6 pr-3 text-sm text-[hsl(var(--muted-foreground))]"
                >
                  没有符合筛选条件的地图
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        <MapPagination
          page={currentPage}
          pageCount={pageCount}
          pageSize={pageSize}
          total={visibleMaps.length}
          start={pageStart}
          end={pageEnd}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Dialog
        open={creating || !!editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={editing ? `编辑地图 ${editing.map_id}` : "新建地图"}
        className="w-[min(960px,96vw)]"
      >
        <MapEditor
          initial={editing ?? { sort_order: 0 }}
          continents={bundle.bundle.continents}
          existingMapIDs={bundle.bundle.maps.map((m) => m.map_id)}
          sameGroupMaps={sameGroupOf(editing ?? {})}
          submitting={upMut.isPending || continueMut.isPending}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={(input) => upMut.mutate(input)}
          onSubmitContinue={
            creating
              ? (input) => continueMut.mutate(input)
              : undefined
          }
        />
      </Dialog>

      <CopyDrawer
        open={copyOpen}
        onOpenChange={setCopyOpen}
        maps={bundle.bundle.maps}
        continents={bundle.bundle.continents}
        submitting={copyMut.isPending || batchCreateMut.isPending}
        onConfirm={(plan) => copyMut.mutate(plan)}
        onConfirmBatch={(payload) => {
          if (payload.upserts.length === 0) {
            toast.error("没有可创建的地图（全部冲突且未勾覆盖）");
            return;
          }
          batchCreateMut.mutate(payload.upserts);
        }}
      />
    </div>
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

async function upsertMapSubmission(gameID: string, input: MapEditorSubmitInput) {
  const inputs = Array.isArray(input) ? input : [input];
  const saved: string[] = [];
  for (const item of inputs) {
    try {
      await upsertMap(gameID, item);
      saved.push(item.map_id);
    } catch (e) {
      throw new Error(
        `${item.map_id}: ${e instanceof Error ? e.message : String(e)}（已完成 ${saved.length}/${inputs.length}）`,
        { cause: e },
      );
    }
  }
  return saved;
}

function MapPagination({
  page,
  pageCount,
  pageSize,
  total,
  start,
  end,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageCount: number;
  pageSize: (typeof MAP_PAGE_SIZE_OPTIONS)[number];
  total: number;
  start: number;
  end: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: (typeof MAP_PAGE_SIZE_OPTIONS)[number]) => void;
}) {
  return (
    <div className="mt-3 flex items-center justify-between gap-3 border-t border-[hsl(var(--border))] pt-3">
      <div className="text-xs text-[hsl(var(--muted-foreground))]">
        {total > 0 ? `显示 ${start}-${end} / ${total} 张地图` : "显示 0 / 0 张地图"}
      </div>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          每页
          <select
            value={pageSize}
            onChange={(e) =>
              onPageSizeChange(
                Number(e.target.value) as (typeof MAP_PAGE_SIZE_OPTIONS)[number],
              )
            }
            className="h-8 rounded border border-[hsl(var(--input))] bg-transparent px-2 text-xs text-[hsl(var(--foreground))]"
          >
            {MAP_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">
          {page}/{pageCount}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 w-8 px-0"
          disabled={page <= 1}
          aria-label="第一页"
          onClick={() => onPageChange(1)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 w-8 px-0"
          disabled={page <= 1}
          aria-label="上一页"
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 w-8 px-0"
          disabled={page >= pageCount}
          aria-label="下一页"
          onClick={() => onPageChange(Math.min(pageCount, page + 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 w-8 px-0"
          disabled={page >= pageCount}
          aria-label="最后一页"
          onClick={() => onPageChange(pageCount)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function MapsFilterBar({
  filters,
  continents,
  onChange,
}: {
  filters: MapFilters;
  continents: GameContinent[];
  onChange: (next: MapFilters) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
      <Field label="搜索">
        <Input
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="map_id 或 name"
        />
      </Field>
      <Field label="大陆">
        <select
          value={filters.continent}
          onChange={(e) => onChange({ ...filters, continent: e.target.value })}
          className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
        >
          <option value="">全部</option>
          {continents.map((c) => (
            <option key={c.continent_id} value={c.continent_id}>
              {c.name || c.continent_id}
            </option>
          ))}
        </select>
      </Field>
      <label className="flex items-end gap-2 text-xs">
        <input
          type="checkbox"
          checked={filters.selectableOnly}
          onChange={(e) =>
            onChange({ ...filters, selectableOnly: e.target.checked })
          }
        />
        仅可挂
      </label>
      <label className="flex items-end gap-2 text-xs">
        <input
          type="checkbox"
          checked={filters.orphansOnly}
          onChange={(e) =>
            onChange({ ...filters, orphansOnly: e.target.checked })
          }
        />
        仅孤立
      </label>
      <label className="flex items-end gap-2 text-xs">
        <input
          type="checkbox"
          checked={filters.unreachableOnly}
          onChange={(e) =>
            onChange({ ...filters, unreachableOnly: e.target.checked })
          }
        />
        仅不可达
      </label>
    </div>
  );
}

export function filterMaps(
  maps: GameMapSummary[],
  filters: MapFilters,
): GameMapSummary[] {
  const q = filters.search.trim().toLowerCase();
  return maps.filter((m) => {
    if (q) {
      const hay = `${m.map_id} ${m.name}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filters.continent && m.continent_id !== filters.continent) return false;
    if (filters.selectableOnly && !m.selectable_default) return false;
    if (filters.unreachableOnly && !m.unreachable) return false;
    if (filters.orphansOnly) {
      const edges = Array.isArray(m.edges) ? m.edges : [];
      const hasEdges = edges.length > 0;
      const isReferenced = maps.some((other) => {
        if (other.map_id === m.map_id) return false;
        const otherEdges = Array.isArray(other.edges) ? other.edges : [];
        return otherEdges.some((edge) => {
          if (typeof edge === "string") return edge === m.map_id;
          if (edge && typeof edge === "object") {
            const e = edge as { to?: string; target?: string; map_id?: string };
            return e.to === m.map_id || e.target === m.map_id || e.map_id === m.map_id;
          }
          return false;
        });
      });
      if (hasEdges || isReferenced) return false;
    }
    return true;
  });
}

// ===== Modules =====

function ModulesTab({ gameID }: { gameID: string }) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["game-modules", gameID],
    queryFn: () => listGameModules(gameID),
  });
  const bundleQ = useQuery({
    queryKey: ["game-bundle", gameID],
    queryFn: () => getGameBundle(gameID),
  });
  const [editing, setEditing] = useState<GameModuleEntry | null>(null);
  const [refsModule, setRefsModule] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: ({ module, text }: { module: string; text: string }) =>
      updateGameModuleConfig(gameID, module, text),
    onSuccess: () => {
      toast.success("已保存游戏模块默认");
      queryClient.invalidateQueries({ queryKey: ["game-modules", gameID] });
      setEditing(null);
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  const clearMut = useMutation({
    mutationFn: (module: string) => clearGameModuleConfig(gameID, module),
    onSuccess: () => {
      toast.success("已清除游戏覆盖");
      queryClient.invalidateQueries({ queryKey: ["game-modules", gameID] });
    },
    onError: (e) => toast.error(`清除失败：${(e as Error).message}`),
  });

  return (
    <div className="space-y-4">
      <QueryView query={q} isEmpty={(d) => d.modules.length === 0}>
        {(data) => (
          <Card className="p-4">
            <div className="mb-3">
              <CardTitle>模块默认配置</CardTitle>
              <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                游戏层保存默认策略，账号和实例只保存差异覆盖。关闭“加载”后，新连接拉取 socket 模块包时不会加载该模块。
              </p>
            </div>
            <div className="overflow-x-auto">
            <table className="min-w-[860px] w-full text-sm">
              <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="py-2 pr-3">模块</th>
                  <th className="py-2 pr-3">游戏覆盖</th>
                  <th className="py-2 pr-3">加载状态</th>
                  <th className="py-2 pr-3">账号覆盖</th>
                  <th className="py-2 pr-3">实例覆盖</th>
                  <th className="py-2 pr-3">运行态</th>
                  <th className="py-2 pr-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {data.modules.map((item) => (
                  <tr
                    key={item.module}
                    className="border-b border-[hsl(var(--border))]/60 align-top"
                  >
                    <td className="py-2 pr-3 font-mono text-xs">{item.module}</td>
                    <td className="py-2 pr-3">
                      {item.game_override ? (
                        <StatusBadge variant="ok">已配置</StatusBadge>
                      ) : (
                        <StatusBadge variant="neutral">继承空默认</StatusBadge>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <StatusBadge variant={moduleGameLoaded(item) ? "ok" : "neutral"}>
                        {moduleGameLoaded(item) ? "加载" : "不加载"}
                      </StatusBadge>
                    </td>
                    <td className="py-2 pr-3">{item.account_override_count}</td>
                    <td className="py-2 pr-3">{item.instance_override_count}</td>
                    <td className="py-2 pr-3 text-xs">
                      <span>{item.runtime.running} 运行中</span>
                      <span className="ml-2 text-[hsl(var(--muted-foreground))]">
                        {item.runtime.drift} 配置偏差
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(item)}
                          title="编辑游戏默认配置"
                        >
                          <Pencil className="mr-1 h-3.5 w-3.5" />
                          编辑
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRefsModule(item.module)}
                          title="查看账号和实例覆盖"
                        >
                          <Layers className="mr-1 h-3.5 w-3.5" />
                          覆盖
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!item.game_override || clearMut.isPending}
                          onClick={() => clearMut.mutate(item.module)}
                          title="清除游戏覆盖"
                        >
                          <Trash2 className="mr-1 h-3.5 w-3.5" />
                          清除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>
        )}
      </QueryView>

      <Dialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        title={editing ? `编辑 ${editing.module}` : undefined}
        className="w-[min(760px,92vw)]"
      >
        {editing ? (
          <GameModuleEditor
            module={editing}
            bundle={bundleQ.data}
            submitting={saveMut.isPending}
            onCancel={() => setEditing(null)}
            onSubmit={(text) =>
              saveMut.mutate({ module: editing.module, text })
            }
          />
        ) : null}
      </Dialog>

      <Dialog
        open={!!refsModule}
        onOpenChange={(open) => {
          if (!open) setRefsModule(null);
        }}
        title={refsModule ? `${refsModule} 覆盖引用` : undefined}
        className="w-[min(900px,92vw)]"
      >
        {refsModule ? (
          <GameModuleReferences gameID={gameID} module={refsModule} />
        ) : null}
      </Dialog>
    </div>
  );
}

function GameModuleEditor({
  module,
  bundle,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  bundle?: GameBundleResponse;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  if (module.module === "auto_map_hunt") {
    return (
      <GameAutoMapHuntEditor
        module={module}
        bundle={bundle}
        submitting={submitting}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  }
  if (module.module === "continent_flee") {
    return (
      <GameContinentFleeEditor
        module={module}
        submitting={submitting}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  }
  if (module.module === "auto_summon") {
    return (
      <GameAutoSummonEditor
        module={module}
        submitting={submitting}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  }
  if (module.module === "auto_auction_bid") {
    return (
      <GameAutoAuctionBidEditor
        module={module}
        submitting={submitting}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  }
  if (module.module === "auto_suoyao") {
    return (
      <GameAutoSuoYaoEditor
        module={module}
        submitting={submitting}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  }
  if (module.module === "full_screen_pull_monster") {
    return (
      <GamePullMonsterEditor
        module={module}
        submitting={submitting}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  }
  if (module.module === "auto_wuxing") {
    return (
      <GameLoadOnlyEditor
        module={module}
        title="自动五行"
        submitting={submitting}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  }
  return (
    <GameRawJSONEditor
      module={module}
      submitting={submitting}
      onCancel={onCancel}
      onSubmit={onSubmit}
    />
  );
}

function GameAutoMapHuntEditor({
  module,
  bundle,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  bundle?: GameBundleResponse;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const parsed = useMemo(
    () => parseAutoMapHuntDesired(module.game_override_json || "{}"),
    [module.game_override_json],
  );
  const [load, setLoad] = useState(parsed.load);
  const [enabled, setEnabled] = useState(parsed.enabled);
  const [cutUpgradeEnabled, setCutUpgradeEnabled] = useState(parsed.cutUpgradeEnabled);
  const [selectAllMaps, setSelectAllMaps] = useState(parsed.selectAllMaps);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(parsed.selectedMapIds),
  );

  useEffect(() => {
    setLoad(parsed.load);
    setEnabled(parsed.enabled);
    setCutUpgradeEnabled(parsed.cutUpgradeEnabled);
    setSelectAllMaps(parsed.selectAllMaps);
    setSelected(new Set(parsed.selectedMapIds));
  }, [parsed]);

  function buildDesired() {
    const ordered = bundle
      ? bundle.bundle.maps.map((m) => m.map_id).filter((id) => selected.has(id))
      : parsed.selectedMapIds.filter((id) => selected.has(id));
    return JSON.stringify(
      selectAllMaps
        ? {
            load,
            enabled,
            cutUpgrade: {
              enabled: cutUpgradeEnabled,
              mode: "privilege_map",
              stopAtMultiplier: 8,
            },
            selectAllMaps: true,
          }
        : {
            load,
            enabled,
            cutUpgrade: {
              enabled: cutUpgradeEnabled,
              mode: "privilege_map",
              stopAtMultiplier: 8,
            },
            selectAllMaps: false,
            selectedMapIds: ordered,
          },
      null,
      2,
    );
  }

  return (
    <GameModuleFormShell
      module={module}
    >
      <ModuleConfigFields
        footer={<GameModuleFormActions submitting={submitting} onCancel={onCancel} />}
        onSubmit={() => onSubmit(buildDesired())}
      >
        <ModuleToggle
          label="加载自动下图模块"
          checked={load}
          disabled={submitting}
          onCheckedChange={setLoad}
        />
        <ModuleToggle
          label="启用自动挂机"
          checked={enabled}
          disabled={submitting}
          onCheckedChange={setEnabled}
        />
        <ModuleToggle
          label="升级切割"
          checked={cutUpgradeEnabled}
          disabled={submitting}
          onCheckedChange={setCutUpgradeEnabled}
        />
        <ModuleToggle
          label="动态全选地图"
          checked={selectAllMaps}
          disabled={submitting}
          onCheckedChange={setSelectAllMaps}
        />
        {bundle ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={submitting || selectAllMaps}
                onClick={() => setSelected(new Set(selectableAutoMapIDs(bundle)))}
              >
                全选可用地图
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={submitting || selectAllMaps}
                onClick={() => {
                  setSelected((prev) => {
                    const selectable = selectableAutoMapIDs(bundle);
                    return new Set(selectable.filter((id) => !prev.has(id)));
                  });
                }}
              >
                反选可用地图
              </Button>
            </div>
            <GameAutoMapSelector
              bundle={bundle}
              selected={selected}
              disabled={selectAllMaps}
              onSelectedChange={setSelected}
            />
          </div>
        ) : (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            地图目录加载中；可先保存加载和启用状态，地图选择会在目录可用后显示。
          </p>
        )}
      </ModuleConfigFields>
    </GameModuleFormShell>
  );
}

function GameContinentFleeEditor({
  module,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  return (
    <GameModuleFormShell module={module}>
      <ContinentFleeConfigForm
        rawConfig={module.game_override_json || "{}"}
        scope="game"
        disabled={submitting}
        footer={<GameModuleFormActions submitting={submitting} onCancel={onCancel} />}
        onSubmit={onSubmit}
      />
    </GameModuleFormShell>
  );
}

function GameAutoSummonEditor({
  module,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  return (
    <GameModuleFormShell module={module}>
      <AutoSummonConfigForm
        rawConfig={module.game_override_json || "{}"}
        scope="game"
        disabled={submitting}
        footer={<GameModuleFormActions submitting={submitting} onCancel={onCancel} />}
        onSubmit={onSubmit}
      />
    </GameModuleFormShell>
  );
}

function GameAutoAuctionBidEditor({
  module,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  return (
    <GameModuleFormShell module={module}>
      <AutoAuctionBidConfigForm
        rawConfig={module.game_override_json || "{}"}
        scope="game"
        disabled={submitting}
        footer={<GameModuleFormActions submitting={submitting} onCancel={onCancel} />}
        onSubmit={onSubmit}
      />
    </GameModuleFormShell>
  );
}

function GameAutoSuoYaoEditor({
  module,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  return (
    <GameModuleFormShell module={module}>
      <AutoSuoYaoConfigForm
        rawConfig={module.game_override_json || "{}"}
        scope="game"
        disabled={submitting}
        footer={<GameModuleFormActions submitting={submitting} onCancel={onCancel} />}
        onSubmit={onSubmit}
      />
    </GameModuleFormShell>
  );
}

function GamePullMonsterEditor({
  module,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  return (
    <GameModuleFormShell module={module}>
      <PullMonsterConfigForm
        rawConfig={module.game_override_json || "{}"}
        scope="game"
        disabled={submitting}
        footer={<GameModuleFormActions submitting={submitting} onCancel={onCancel} />}
        onSubmit={onSubmit}
      />
    </GameModuleFormShell>
  );
}

function GameLoadOnlyEditor({
  module,
  title,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  title: string;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const parsed = useMemo(
    () => parseJSONRecord(module.game_override_json || "{}"),
    [module.game_override_json],
  );
  const [load, setLoad] = useState(moduleGameLoaded(module));

  useEffect(() => {
    setLoad(moduleGameLoaded(module));
  }, [module]);

  return (
    <GameModuleFormShell
      module={module}
    >
      <ModuleConfigFields
        footer={<GameModuleFormActions submitting={submitting} onCancel={onCancel} />}
        onSubmit={() => onSubmit(JSON.stringify({ ...parsed, load }, null, 2))}
      >
        <ModuleToggle
          label={`加载${title}模块`}
          checked={load}
          disabled={submitting}
          onCheckedChange={setLoad}
        />
      </ModuleConfigFields>
    </GameModuleFormShell>
  );
}

function GameRawJSONEditor({
  module,
  submitting,
  onCancel,
  onSubmit,
}: {
  module: GameModuleEntry;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const [load, setLoad] = useState(moduleGameLoaded(module));
  const [text, setText] = useState(() => rawConfigTextWithoutLoad(module.game_override_json || "{}"));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoad(moduleGameLoaded(module));
    setText(rawConfigTextWithoutLoad(module.game_override_json || "{}"));
    setError(null);
  }, [module]);

  function update(next: string) {
    setText(next);
    try {
      parseRawConfigText(next);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!error) {
          const parsed = parseRawConfigText(text);
          onSubmit(JSON.stringify({ ...parsed, load }, null, 2));
        }
      }}
      noValidate
    >
      <ModuleToggle
        label={`加载${module.module}模块`}
        checked={load}
        disabled={submitting}
        onCheckedChange={setLoad}
      />
      <Field error={error ?? undefined}>
        <Textarea
          rows={18}
          value={text}
          onChange={(e) => update(e.target.value)}
          spellCheck={false}
          className="font-mono text-xs"
        />
      </Field>
      <div className="grid gap-2 text-xs text-[hsl(var(--muted-foreground))] sm:grid-cols-2">
        <div>内置默认：{module.default_config_available ? "已读取" : "暂不可用"}</div>
        <div>当前摘要：{moduleConfigSummary(module.effective_config)}</div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          取消
        </Button>
        <Button type="submit" size="sm" disabled={!!error || submitting}>
          {submitting ? "保存中…" : "保存"}
        </Button>
      </div>
    </form>
  );
}

function GameModuleFormShell({
  module,
  children,
}: {
  module: GameModuleEntry;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/25 p-3 text-xs text-[hsl(var(--muted-foreground))]">
        <div>当前摘要：{moduleConfigSummary(module.effective_config)}</div>
        <div>内置默认：{module.default_config_available ? "已读取" : "由 Lua normalize 补齐"}</div>
      </div>
      {children}
    </div>
  );
}

function GameModuleFormActions({
  submitting,
  onCancel,
}: {
  submitting: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex justify-end gap-2">
      <Button type="button" size="sm" variant="outline" onClick={onCancel}>
        取消
      </Button>
      <Button type="submit" size="sm" disabled={submitting}>
        {submitting ? "保存中…" : "保存"}
      </Button>
    </div>
  );
}

function GameAutoMapSelector({
  bundle,
  selected,
  disabled = false,
  onSelectedChange,
}: {
  bundle: GameBundleResponse;
  selected: Set<string>;
  disabled?: boolean;
  onSelectedChange: (value: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
}) {
  return (
    <div className="max-h-[52vh] space-y-4 overflow-auto pr-1">
      {bundle.bundle.continents.map((continent) => {
        const maps = bundle.bundle.maps.filter(
          (m) => m.continent_id === continent.continent_id,
        );
        if (maps.length === 0) return null;
        return (
          <section key={continent.continent_id} className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium">{continent.name}</h3>
              {!continent.enabled ? <StatusBadge variant="neutral">停用</StatusBadge> : null}
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              {maps.map((map) => {
                const blocked = !continent.enabled || map.unreachable;
                const checked = selected.has(map.map_id);
                const itemDisabled = disabled || (blocked && !checked);
                return (
                  <label
                    key={map.map_id}
                    className="flex items-start gap-2 rounded border border-[hsl(var(--border))] p-2 text-xs"
                  >
                    <Checkbox
                      checked={checked}
                      disabled={itemDisabled}
                      onCheckedChange={(value) => {
                        onSelectedChange((prev) => {
                          const next = new Set(prev);
                          if (value === true) next.add(map.map_id);
                          else next.delete(map.map_id);
                          return next;
                        });
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block font-medium">{map.name}</span>
                      <span className="block break-all font-mono text-[hsl(var(--muted-foreground))]">
                        {map.map_id}
                      </span>
                      {itemDisabled && blocked ? (
                        <span className="block text-[hsl(var(--muted-foreground))]">
                          {map.unreachable_reason || "continent_disabled"}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function GameModuleReferences({
  gameID,
  module,
}: {
  gameID: string;
  module: string;
}) {
  const q = useQuery({
    queryKey: ["game-module-references", gameID, module],
    queryFn: () => getGameModuleReferences(gameID, module),
  });
  return (
    <QueryView query={q}>
      {(data) => <GameModuleReferencesContent data={data} />}
    </QueryView>
  );
}

function GameModuleReferencesContent({
  data,
}: {
  data: GameModuleReferencesResponse;
}) {
  return (
    <div className="space-y-4">
      <section>
        <h3 className="mb-2 text-sm font-medium">
          账号覆盖（{data.references.accounts.length}）
        </h3>
        {data.references.accounts.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">无账号覆盖。</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-[520px] w-full text-xs">
            <thead className="border-b border-[hsl(var(--border))] text-left text-[hsl(var(--muted-foreground))]">
              <tr>
                <th className="py-2 pr-3">账号</th>
                <th className="py-2 pr-3">启用</th>
                <th className="py-2 pr-3">配置一致性</th>
                <th className="py-2 pr-3">运行态</th>
              </tr>
            </thead>
            <tbody>
              {data.references.accounts.map((ref) => (
                <tr key={ref.account_id} className="border-b border-[hsl(var(--border))]/60">
                  <td className="py-1.5 pr-3">
                    #{ref.account_id} {ref.game_account}
                  </td>
                  <td className="py-1.5 pr-3">
                    {formatModuleDesiredEnabled(ref.desired_enabled)}
                  </td>
                  <td className="py-1.5 pr-3">
                    {formatModuleConfigDrift(ref.config_drift)}
                  </td>
                  <td className="py-1.5 pr-3">
                    {formatModuleRuntimeStatus(ref.runtime_status) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-sm font-medium">
          实例覆盖（{data.references.instances.length}）
        </h3>
        {data.references.instances.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">无实例覆盖。</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-[720px] w-full text-xs">
            <thead className="border-b border-[hsl(var(--border))] text-left text-[hsl(var(--muted-foreground))]">
              <tr>
                <th className="py-2 pr-3">实例</th>
                <th className="py-2 pr-3">账号</th>
                <th className="py-2 pr-3">角色</th>
                <th className="py-2 pr-3">区服</th>
              </tr>
            </thead>
            <tbody>
              {data.references.instances.map((ref) => (
                <tr key={ref.instance_id} className="border-b border-[hsl(var(--border))]/60">
                  <td className="max-w-[280px] break-all py-1.5 pr-3 font-mono">{ref.instance_id}</td>
                  <td className="py-1.5 pr-3">{ref.game_account || `#${ref.account_id}`}</td>
                  <td className="py-1.5 pr-3">{ref.role_name || "—"}</td>
                  <td className="py-1.5 pr-3">{ref.server_name || ref.server_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  );
}

function moduleConfigSummary(value: Record<string, unknown>) {
  const keys = Object.keys(value || {});
  if (keys.length === 0) return "{}";
  return keys
    .slice(0, 4)
    .map((key) => {
      const item = value[key];
      if (Array.isArray(item)) return `${key}[${item.length}]`;
      if (typeof item === "boolean") return `${key}=${item ? "true" : "false"}`;
      if (typeof item === "number" || typeof item === "string") return `${key}=${item}`;
      return `${key}{}`;
    })
    .join(" · ") + (keys.length > 4 ? ` · +${keys.length - 4}` : "");
}

function moduleGameLoaded(module: GameModuleEntry) {
  if (!module.game_override) return true;
  const gameOverride = parseJSONRecord(module.game_override_json || "{}");
  if (typeof gameOverride.load === "boolean") {
    return gameOverride.load;
  }
  if (typeof module.effective_config?.load === "boolean") {
    return module.effective_config.load;
  }
  return true;
}

function rawConfigTextWithoutLoad(raw: string) {
  const parsed = parseJSONRecord(raw);
  delete parsed.load;
  return JSON.stringify(parsed, null, 2);
}

function parseRawConfigText(raw: string): Record<string, unknown> {
  const parsed = JSON.parse(raw || "{}");
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("必须是 JSON object");
  }
  return parsed as Record<string, unknown>;
}

function selectableAutoMapIDs(bundle: GameBundleResponse) {
  const enabledContinents = new Map(
    bundle.bundle.continents.map((continent) => [continent.continent_id, continent.enabled]),
  );
  return bundle.bundle.maps
    .filter((map) => enabledContinents.get(map.continent_id) !== false && !map.unreachable)
    .map((map) => map.map_id);
}

// ===== Watch lists (货币 / 物品) =====

function WatchListTab({
  kind,
  bundle,
}: {
  kind: "currencies" | "materials";
  bundle: GameBundleResponse;
}) {
  const queryClient = useQueryClient();
  const gameID = bundle.bundle.game_id;
  const items = bundle.bundle[kind];
  const [editing, setEditing] = useState<CurrencyWatchRecord | MaterialWatchRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const title = kind === "currencies" ? "货币监控" : "物品清单";

  const upMut = useMutation({
    mutationFn: async (input: {
      id?: number;
      numeric_id: number;
      label: string;
      sort_order: number;
      item_name?: string;
    }) => {
      if (kind === "currencies") {
        await (input.id
          ? updateCurrencyWatch(input.id, { game_id: gameID, ...input })
          : createCurrencyWatch({ game_id: gameID, ...input }));
        return;
      }
      await (input.id
        ? updateMaterialWatch(input.id, { game_id: gameID, ...input })
        : createMaterialWatch({ game_id: gameID, ...input }));
    },
    onSuccess: () => {
      toast.success("已保存");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
      setCreating(false);
      setEditing(null);
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  const delMut = useMutation({
    mutationFn: (id: number) =>
      kind === "currencies" ? deleteCurrencyWatch(id) : deleteMaterialWatch(id),
    onSuccess: () => {
      toast.success("已删除");
      queryClient.invalidateQueries({ queryKey: ["game-bundle", gameID] });
    },
    onError: (e) => toast.error(`删除失败：${(e as Error).message}`),
  });

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          {kind === "materials" ? (
            <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
              这里统一维护材料数量统计和市场分析价格监控使用的物品清单。
            </p>
          ) : null}
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          新增
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">未配置。</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
            <tr>
              <th className="py-2 pr-3">id</th>
              <th className="py-2 pr-3">
                {kind === "currencies" ? "currency_id" : "item_id"}
              </th>
              <th className="py-2 pr-3">label</th>
              <th className="py-2 pr-3">排序</th>
              <th className="py-2 pr-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const numericID =
                kind === "currencies"
                  ? (it as { currency_id: number }).currency_id
                  : (it as { item_id: number }).item_id;
              return (
                <tr
                  key={it.id}
                  className="border-b border-[hsl(var(--border))]/60"
                >
                  <td className="py-1.5 pr-3 font-mono text-xs">{it.id}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{numericID}</td>
                  <td className="py-1.5 pr-3">{it.label || "—"}</td>
                  <td className="py-1.5 pr-3">{it.sort_order}</td>
                  <td className="py-1.5 pr-3 space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing(it)}>
                      编辑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (
                          typeof window !== "undefined" &&
                          window.confirm(`删除 ${numericID}？`)
                        ) {
                          delMut.mutate(it.id);
                        }
                      }}
                    >
                      删除
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <Dialog
        open={creating || !!editing}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={editing ? `编辑 ${title}` : `新增 ${title}`}
      >
        <WatchListForm
          kind={kind}
          initial={editing}
          submitting={upMut.isPending}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={(input) => upMut.mutate(input)}
        />
      </Dialog>
    </Card>
  );
}

function WatchListForm({
  kind,
  initial,
  submitting,
  onCancel,
  onSubmit,
}: {
  kind: "currencies" | "materials";
  initial: CurrencyWatchRecord | MaterialWatchRecord | null;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (input: {
    id?: number;
    numeric_id: number;
    label: string;
    sort_order: number;
    item_name?: string;
  }) => void;
}) {
  const numericID =
    initial && kind === "currencies"
      ? (initial as CurrencyWatchRecord).currency_id
      : initial
        ? (initial as MaterialWatchRecord).item_id
        : 0;
  const [rawID, setRawID] = useState(numericID ? String(numericID) : "");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [itemName, setItemName] = useState("");
  const [sortOrder, setSortOrder] = useState(String(initial?.sort_order ?? 0));
  const parsedID = Number(rawID);
  const validID = Number.isInteger(parsedID) && parsedID > 0;
  const validMaterialName = kind === "materials" && !initial && itemName.trim() !== "";
  const canSubmit = kind === "currencies" ? validID : validID || validMaterialName;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      id: initial?.id,
      numeric_id: validID ? parsedID : 0,
      label,
      sort_order: Number(sortOrder) || 0,
      item_name: validMaterialName ? itemName.trim() : undefined,
    });
  }

  const idLabel =
    kind === "currencies" ? "currency_id" : initial ? "item_id" : "item_id（可选）";
  const itemNameInputID = "watch-list-item-name";
  const numericInputID =
    kind === "currencies" ? "watch-list-currency-id" : "watch-list-item-id";

  return (
    <form className="space-y-3" onSubmit={submit} noValidate>
      {kind === "materials" && !initial ? (
        <Field label="物品名" htmlFor={itemNameInputID}>
          <Input
            id={itemNameInputID}
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
        </Field>
      ) : null}
      <Field
        label={idLabel}
        htmlFor={numericInputID}
        error={rawID && !validID ? "必须是正整数" : undefined}
      >
        <Input
          id={numericInputID}
          type="number"
          value={rawID}
          onChange={(e) => setRawID(e.target.value)}
          disabled={!!initial}
        />
      </Field>
      <Field label="label">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} />
      </Field>
      <Field label="sort_order">
        <Input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </Field>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          取消
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit || submitting}>
          {submitting ? "保存中…" : "保存"}
        </Button>
      </div>
    </form>
  );
}

// ===== Profit rules =====

function ProfitRulesTab({
  gameID,
  bundle,
}: {
  gameID: string;
  bundle: GameBundleResponse;
}) {
  const queryClient = useQueryClient();
  const [windowMode, setWindowMode] = useState<"60m" | "24h" | "today">("60m");
  const [serverID, setServerID] = useState("");
  const serverFilter = serverID.trim();
  const rankingQuery = useMemo(
    () => ({
      window: windowMode,
      serverID: serverFilter,
      limit: 20,
    }),
    [serverFilter, windowMode],
  );
  const instanceRankingQ = useQuery({
    queryKey: ["game-profit-instances", gameID, rankingQuery],
    queryFn: () => getGameProfitInstances(gameID, rankingQuery),
    refetchInterval: 30_000,
  });
  const mapRankingQ = useQuery({
    queryKey: ["game-profit-maps", gameID, rankingQuery],
    queryFn: () => getGameProfitMaps(gameID, rankingQuery),
    refetchInterval: 30_000,
  });
  const itemRankingQ = useQuery({
    queryKey: ["game-profit-items", gameID, rankingQuery],
    queryFn: () => getGameProfitItems(gameID, rankingQuery),
    refetchInterval: 30_000,
  });
  const valueQ = useQuery({
    queryKey: ["profit-value-rules", gameID],
    queryFn: () => listProfitValueRules(gameID),
  });
  const sourceQ = useQuery({
    queryKey: ["profit-source-rules", gameID],
    queryFn: () => listProfitSourceRules(gameID),
  });
  const valueMut = useMutation({
    mutationFn: (input: ProfitValueRuleInput) => upsertProfitValueRule(gameID, input),
    onSuccess: () => {
      toast.success("已保存估值规则");
      void queryClient.invalidateQueries({ queryKey: ["profit-value-rules", gameID] });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });
  const sourceMut = useMutation({
    mutationFn: (input: ProfitSourceRuleInput) => upsertProfitSourceRule(gameID, input),
    onSuccess: () => {
      toast.success("已保存来源规则");
      void queryClient.invalidateQueries({ queryKey: ["profit-source-rules", gameID] });
    },
    onError: (e) => toast.error(`保存失败：${(e as Error).message}`),
  });

  useSSESubscription("instance.profit_updated", (payload) => {
    if (payload.game_id !== gameID) return;
    if (serverFilter && payload.server_id !== serverFilter) return;
    void queryClient.invalidateQueries({ queryKey: ["game-profit-instances", gameID] });
    void queryClient.invalidateQueries({ queryKey: ["game-profit-maps", gameID] });
    void queryClient.invalidateQueries({ queryKey: ["game-profit-items", gameID] });
  });

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle>收益排行</CardTitle>
            <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
              {profitWindowLabel(windowMode)}
              {serverFilter ? ` · 区服 ${serverFilter}` : " · 全区服"}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="窗口">
              <select
                className="h-9 rounded-md border border-[hsl(var(--border))] bg-transparent px-2 text-sm"
                value={windowMode}
                onChange={(e) => setWindowMode(e.target.value as "60m" | "24h" | "today")}
              >
                <option value="60m">最近60分钟</option>
                <option value="24h">最近24小时</option>
                <option value="today">今日</option>
              </select>
            </Field>
            <Field label="server_id">
              <Input
                value={serverID}
                onChange={(e) => setServerID(e.target.value)}
                placeholder="全部区服"
              />
            </Field>
          </div>
        </div>
        <div className="mt-4 grid gap-4 xl:grid-cols-3">
          <QueryView query={instanceRankingQ} isEmpty={(d) => d.items.length === 0}>
            {(data) => <ProfitInstanceRankingTable items={data.items} />}
          </QueryView>
          <QueryView query={mapRankingQ} isEmpty={(d) => d.items.length === 0}>
            {(data) => <ProfitMapRankingTable items={data.items} />}
          </QueryView>
          <QueryView query={itemRankingQ} isEmpty={(d) => d.items.length === 0}>
            {(data) => <ProfitItemRankingTable items={data.items} />}
          </QueryView>
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
        <Card className="p-4">
          <CardTitle>估值规则</CardTitle>
          <ProfitValueRuleForm
            currencies={bundle.bundle.currencies}
            materials={bundle.bundle.materials}
            submitting={valueMut.isPending}
            onSubmit={(input) => valueMut.mutate(input)}
          />
          <QueryView query={valueQ} isEmpty={(d) => d.items.length === 0}>
            {(data) => <ProfitValueRuleTable items={data.items} />}
          </QueryView>
        </Card>
        <Card className="p-4">
          <CardTitle>来源分类</CardTitle>
          <ProfitSourceRuleForm
            submitting={sourceMut.isPending}
            onSubmit={(input) => sourceMut.mutate(input)}
          />
          <QueryView query={sourceQ} isEmpty={(d) => d.items.length === 0}>
            {(data) => <ProfitSourceRuleTable items={data.items} />}
          </QueryView>
        </Card>
      </div>
    </div>
  );
}

function profitWindowLabel(value: "60m" | "24h" | "today"): string {
  if (value === "60m") return "最近60分钟";
  if (value === "24h") return "最近24小时";
  return "今日";
}

function ProfitInstanceRankingTable({
  items,
}: {
  items: ProfitInstanceAggregate[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[760px] w-full text-sm">
        <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
          <tr>
            <th className="py-2 pr-3">实例</th>
            <th className="py-2 pr-3">区服</th>
            <th className="py-2 pr-3">active</th>
            <th className="py-2 pr-3">净值/h</th>
            <th className="py-2 pr-3">货币/h</th>
            <th className="py-2 pr-3">材料/h</th>
            <th className="py-2 pr-3">样本</th>
            <th className="py-2 pr-3">更新时间</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.instance_id}
              className="border-b border-[hsl(var(--border))]/60 align-top"
            >
              <td className="py-2 pr-3">
                <Link
                  to="/instances/$instanceId"
                  params={{ instanceId: item.instance_id }}
                  className="font-medium hover:underline"
                >
                  {item.role_name || item.instance_id}
                </Link>
                <div className="mt-0.5 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                  {item.instance_id}
                </div>
              </td>
              <td className="py-2 pr-3 font-mono text-xs">{item.server_id || "—"}</td>
              <td className="py-2 pr-3 font-mono">
                {formatDurationSec(item.active_seconds)}
              </td>
              <td className="py-2 pr-3 font-mono">
                {formatProfitRate(item.net_value_per_hour)}
              </td>
              <td className="py-2 pr-3">
                <ProfitCurrencyRateMini items={item.totals.currencies} />
              </td>
              <td className="py-2 pr-3">
                <ProfitMaterialRateMini items={item.totals.materials} />
              </td>
              <td className="py-2 pr-3">
                <StatusBadge variant={item.sample_status === "ok" ? "ok" : "warn"}>
                  {item.sample_status === "ok" ? "有效" : "不足"}
                </StatusBadge>
              </td>
              <td className="py-2 pr-3 text-xs text-[hsl(var(--muted-foreground))]">
                {item.updated_at ? formatMs(item.updated_at) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfitMapRankingTable({ items }: { items: ProfitMapAggregate[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[680px] w-full text-sm">
        <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
          <tr>
            <th className="py-2 pr-3">地图</th>
            <th className="py-2 pr-3">active</th>
            <th className="py-2 pr-3">事件</th>
            <th className="py-2 pr-3">净值/h</th>
            <th className="py-2 pr-3">货币/h</th>
            <th className="py-2 pr-3">材料/h</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.map_id}
              className="border-b border-[hsl(var(--border))]/60 align-top"
            >
              <td className="py-2 pr-3">
                <div className="font-medium">{item.map_name || item.map_id || "未识别地图"}</div>
                <div className="mt-0.5 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                  {item.map_id || "unknown"}
                </div>
              </td>
              <td className="py-2 pr-3 font-mono">
                {formatDurationSec(item.active_seconds)}
              </td>
              <td className="py-2 pr-3 font-mono">{compactNumber(item.event_count)}</td>
              <td className="py-2 pr-3 font-mono">
                {formatProfitRate(item.net_value_per_hour)}
              </td>
              <td className="py-2 pr-3">
                <ProfitCurrencyRateMini items={item.currencies} />
              </td>
              <td className="py-2 pr-3">
                <ProfitMaterialRateMini items={item.materials} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfitItemRankingTable({ items }: { items: ProfitItemAggregate[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[560px] w-full text-sm">
        <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
          <tr>
            <th className="py-2 pr-3">物品/货币</th>
            <th className="py-2 pr-3">产出/h</th>
            <th className="py-2 pr-3">净值/h</th>
            <th className="py-2 pr-3">估值</th>
            <th className="py-2 pr-3">置信</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const key = item.kind === "currency" ? item.id : item.key || String(item.item_id || "");
            return (
              <tr
                key={`${item.kind}:${key}`}
                className="border-b border-[hsl(var(--border))]/60 align-top"
              >
                <td className="py-2 pr-3">
                  <div className="font-medium">{item.name || key || "未命名"}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                    {item.kind === "currency" ? `currency:${item.id}` : item.item_id ? `item:${item.item_id}` : item.key}
                  </div>
                </td>
                <td className="py-2 pr-3 font-mono">
                  {item.kind === "currency"
                    ? formatProfitRate(item.net_per_hour || item.gain_per_hour)
                    : `×${compactNumber(item.gain_per_hour || 0)}/h`}
                </td>
                <td className="py-2 pr-3 font-mono">
                  {formatProfitRate(item.value_per_hour)}
                </td>
                <td className="py-2 pr-3 text-xs">
                  {item.unit_value
                    ? `${compactNumber(item.unit_value)} ${item.value_currency || ""}`
                    : "—"}
                </td>
                <td className="py-2 pr-3">
                  {item.confidence === "name_match" ? (
                    <StatusBadge variant="warn">名称匹配</StatusBadge>
                  ) : (
                    <StatusBadge variant="neutral">观测</StatusBadge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProfitCurrencyRateMini({
  items,
}: {
  items: ProfitMapAggregate["currencies"];
}) {
  if (!items || items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <div className="flex min-w-36 flex-col gap-1">
      {items.slice(0, 3).map((item) => (
        <span key={item.id} className="whitespace-nowrap text-xs">
          {item.name || `#${item.id}`}{" "}
          <strong className="font-mono">
            {compactNumber(item.net_per_hour || item.gain_per_hour || 0)}/h
          </strong>
        </span>
      ))}
    </div>
  );
}

function ProfitMaterialRateMini({
  items,
}: {
  items: ProfitMapAggregate["materials"];
}) {
  if (!items || items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <div className="flex min-w-32 flex-col gap-1">
      {items.slice(0, 3).map((item) => (
        <span key={item.key} className="whitespace-nowrap text-xs">
          {item.name || item.key}{" "}
          <strong className="font-mono">
            ×{compactNumber(item.gain_per_hour || 0)}/h
          </strong>
        </span>
      ))}
    </div>
  );
}

function formatProfitRate(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  return `${compactNumber(value)}/h`;
}

function ProfitValueRuleForm({
  currencies,
  materials,
  submitting,
  onSubmit,
}: {
  currencies: CurrencyWatchRecord[];
  materials: MaterialWatchRecord[];
  submitting?: boolean;
  onSubmit: (input: ProfitValueRuleInput) => void;
}) {
  const [kind, setKind] = useState<"currency" | "material">("material");
  const [refID, setRefID] = useState("");
  const [unitValue, setUnitValue] = useState("");
  const [valueCurrency, setValueCurrency] = useState("yuanbao");
  const [source, setSource] = useState("manual");
  const [enabled, setEnabled] = useState(true);
  const options =
    kind === "currency"
      ? currencies.map((item) => ({
          id: String(item.currency_id),
          label: item.label || `#${item.currency_id}`,
        }))
      : materials.map((item) => ({
          id: String(item.item_id),
          label: item.label || `#${item.item_id}`,
        }));
  const parsedUnitValue = Number(unitValue);
  const canSubmit = refID.trim() !== "" && Number.isFinite(parsedUnitValue) && parsedUnitValue >= 0;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      kind,
      ref_id: refID.trim(),
      unit_value: parsedUnitValue,
      value_currency: valueCurrency.trim() || "yuanbao",
      source: source.trim() || "manual",
      enabled,
    });
  }

  return (
    <form className="mt-3 grid gap-3 md:grid-cols-6" onSubmit={submit} noValidate>
      <Field label="类型">
        <select
          className="h-9 rounded-md border border-[hsl(var(--border))] bg-transparent px-2 text-sm"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as "currency" | "material");
            setRefID("");
          }}
        >
          <option value="material">材料</option>
          <option value="currency">货币</option>
        </select>
      </Field>
      <Field label="对象">
        <select
          className="h-9 rounded-md border border-[hsl(var(--border))] bg-transparent px-2 text-sm"
          value={refID}
          onChange={(e) => setRefID(e.target.value)}
        >
          <option value="">选择或手填</option>
          {options.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label} ({item.id})
            </option>
          ))}
        </select>
      </Field>
      <Field label="ref_id">
        <Input value={refID} onChange={(e) => setRefID(e.target.value)} />
      </Field>
      <Field label="单位价值">
        <Input
          type="number"
          min="0"
          step="0.0001"
          value={unitValue}
          onChange={(e) => setUnitValue(e.target.value)}
        />
      </Field>
      <Field label="计价单位">
        <Input value={valueCurrency} onChange={(e) => setValueCurrency(e.target.value)} />
      </Field>
      <Field label="来源">
        <div className="flex items-center gap-2">
          <Input value={source} onChange={(e) => setSource(e.target.value)} />
          <label className="flex items-center gap-1 text-xs">
            <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(Boolean(v))} />
            启用
          </label>
        </div>
      </Field>
      <div className="md:col-span-6">
        <Button size="sm" type="submit" disabled={!canSubmit || submitting}>
          {submitting ? "保存中…" : "保存估值规则"}
        </Button>
      </div>
    </form>
  );
}

function ProfitValueRuleTable({ items }: { items: ProfitValueRule[] }) {
  return (
    <table className="mt-4 w-full text-sm">
      <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
        <tr>
          <th className="py-2 pr-3">类型</th>
          <th className="py-2 pr-3">ref_id</th>
          <th className="py-2 pr-3">单位价值</th>
          <th className="py-2 pr-3">计价</th>
          <th className="py-2 pr-3">来源</th>
          <th className="py-2 pr-3">状态</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="border-b border-[hsl(var(--border))]/60">
            <td className="py-1.5 pr-3">{item.kind === "currency" ? "货币" : "材料"}</td>
            <td className="py-1.5 pr-3 font-mono text-xs">{item.ref_id}</td>
            <td className="py-1.5 pr-3 font-mono">{compactNumber(item.unit_value)}</td>
            <td className="py-1.5 pr-3">{item.value_currency || "yuanbao"}</td>
            <td className="py-1.5 pr-3">{item.source || "manual"}</td>
            <td className="py-1.5 pr-3">
              <StatusBadge variant={item.enabled ? "ok" : "neutral"}>
                {item.enabled ? "启用" : "停用"}
              </StatusBadge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProfitSourceRuleForm({
  submitting,
  onSubmit,
}: {
  submitting?: boolean;
  onSubmit: (input: ProfitSourceRuleInput) => void;
}) {
  const [module, setModule] = useState("auto_map_hunt");
  const [sourceType, setSourceType] = useState("currency_delta");
  const [includeProfit, setIncludeProfit] = useState(true);
  const [includeCost, setIncludeCost] = useState(true);
  const [note, setNote] = useState("");
  const canSubmit = sourceType.trim() !== "";

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      module: module.trim() || "auto_map_hunt",
      source_type: sourceType.trim(),
      include_in_profit: includeProfit,
      include_in_cost: includeCost,
      note,
    });
  }

  return (
    <form className="mt-3 grid gap-3 md:grid-cols-2" onSubmit={submit} noValidate>
      <Field label="module">
        <Input value={module} onChange={(e) => setModule(e.target.value)} />
      </Field>
      <Field label="source_type">
        <Input value={sourceType} onChange={(e) => setSourceType(e.target.value)} />
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={includeProfit} onCheckedChange={(v) => setIncludeProfit(Boolean(v))} />
        计入收益
      </label>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={includeCost} onCheckedChange={(v) => setIncludeCost(Boolean(v))} />
        计入成本
      </label>
      <Field label="备注">
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <div className="flex items-end">
        <Button size="sm" type="submit" disabled={!canSubmit || submitting}>
          {submitting ? "保存中…" : "保存来源规则"}
        </Button>
      </div>
    </form>
  );
}

function ProfitSourceRuleTable({ items }: { items: ProfitSourceRule[] }) {
  return (
    <table className="mt-4 w-full text-sm">
      <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
        <tr>
          <th className="py-2 pr-3">module</th>
          <th className="py-2 pr-3">source_type</th>
          <th className="py-2 pr-3">收益</th>
          <th className="py-2 pr-3">成本</th>
          <th className="py-2 pr-3">备注</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="border-b border-[hsl(var(--border))]/60">
            <td className="py-1.5 pr-3 font-mono text-xs">{item.module}</td>
            <td className="py-1.5 pr-3 font-mono text-xs">{item.source_type}</td>
            <td className="py-1.5 pr-3">
              <StatusBadge variant={item.include_in_profit ? "ok" : "neutral"}>
                {item.include_in_profit ? "计入" : "排除"}
              </StatusBadge>
            </td>
            <td className="py-1.5 pr-3">
              <StatusBadge variant={item.include_in_cost ? "warn" : "neutral"}>
                {item.include_in_cost ? "计入" : "排除"}
              </StatusBadge>
            </td>
            <td className="py-1.5 pr-3">{item.note || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
